import { useApolloClient } from '@apollo/client/react';
import { useSearchParams } from 'react-router-dom';

import type {
  GetKanbanLeadsAgentsQuery,
  GetLeadsQueryVariables,
  LeadsKanbanColumnItemFragment,
} from '@graphql/__generated__/graphql';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { LEADS_KANBAN_COLUMN_ITEM_FRAGMENT } from '@modules/leads/LeadsKanbanColumn';
import { buildLeadsKanbanQueryVars } from '@modules/leads/LeadsKanbanView';
import {
  GET_KANBAN_LEADS_AGENTS,
  GET_KANBAN_LEADS_COUNT,
} from '@modules/leads/leadsQueries';

type UpdateLeadsStageParams = {
  stageId: string;
  leadAgentIds: string[];
  where: GetLeadsQueryVariables['where'];
};

export const useUpdateLeadsStageInCache = () => {
  const apolloClient = useApolloClient();
  const [searchParams] = useSearchParams();

  const { countLimit } = getPaginationVariables(searchParams, undefined, true);

  return ({
    stageId: newStageId,
    leadAgentIds,
    where,
  }: UpdateLeadsStageParams) => {
    if (newStageId == null) {
      return;
    }

    const getQueryVars = (stageId: string) => ({
      query: GET_KANBAN_LEADS_AGENTS,
      variables: buildLeadsKanbanQueryVars(false, where, stageId),
    });

    const getCountQueryVars = (stageId: string) => {
      const queryVars = buildLeadsKanbanQueryVars(false, where, stageId);

      return {
        query: GET_KANBAN_LEADS_COUNT,
        variables: {
          include_leads: queryVars.include_leads,
          where: queryVars.where,
          where_leads: queryVars.where_leads,
          limit: countLimit,
        },
      };
    };

    const leadFragments: LeadsKanbanColumnItemFragment[] = [];
    const oldStagesForLeads = leadAgentIds.reduce<Record<string, string[]>>(
      (acc, id) => {
        const leadRef = apolloClient.cache.identify({
          __typename: 'lead_agents',
          id,
        });

        // Get the current lead data to find its current stage
        const currentLeadAgent = apolloClient.cache.readFragment({
          id: leadRef,
          fragment: LEADS_KANBAN_COLUMN_ITEM_FRAGMENT,
          fragmentName: 'LeadsKanbanColumnItem',
        });

        if (currentLeadAgent?.stage_id != null) {
          leadFragments.push(currentLeadAgent);
          acc[currentLeadAgent.stage_id] = [
            ...(acc[currentLeadAgent.stage_id] ?? []),
            currentLeadAgent.id,
          ];
        }

        return acc;
      },
      {},
    );

    // Batch cache updates in a single operation
    apolloClient.cache.batch({
      update: cache => {
        // 1. Remove from old stage if it exists
        if (Object.keys(oldStagesForLeads).length > 0) {
          for (const [oldStageId, leadIds] of Object.entries(
            oldStagesForLeads,
          )) {
            const oldData = cache.readQuery(getQueryVars(oldStageId));

            if (oldData?.lead_agents != null) {
              cache.writeQuery<GetKanbanLeadsAgentsQuery>({
                ...getQueryVars(oldStageId),
                data: {
                  ...oldData,
                  lead_agents: oldData.lead_agents.filter(
                    agent => !leadIds.includes(agent.id),
                  ),
                },
              });

              // Update count for old stage
              const oldCountData = cache.readQuery(
                getCountQueryVars(oldStageId),
              );

              if (oldCountData) {
                cache.writeQuery({
                  ...getCountQueryVars(oldStageId),
                  data: {
                    lead_agents_aggregate: {
                      aggregate: {
                        count: Math.max(
                          0,
                          (oldCountData.lead_agents_aggregate?.aggregate
                            ?.count ?? 0) - leadIds.length,
                        ),
                      },
                    },
                  },
                });
              }
            }
          }
        }

        // 2. Add to new stage
        const newData = cache.readQuery(getQueryVars(newStageId));

        const updatedLeads = leadFragments.map(leadFragment => ({
          ...leadFragment,
          stage_id: newStageId,
        }));

        cache.writeQuery<GetKanbanLeadsAgentsQuery>({
          ...getQueryVars(newStageId),
          data: {
            ...newData,
            lead_agents: [...(newData?.lead_agents ?? []), ...updatedLeads],
          },
        });

        // Update count for new stage
        const newCountData = cache.readQuery(getCountQueryVars(newStageId));
        if (newCountData) {
          cache.writeQuery({
            ...getCountQueryVars(newStageId),
            data: {
              lead_agents_aggregate: {
                aggregate: {
                  count:
                    (newCountData.lead_agents_aggregate?.aggregate?.count ??
                      0) + updatedLeads.length,
                },
              },
            },
          });
        }

        // 3. Update the lead's own cache entry
        updatedLeads.forEach(lead => {
          const leadRef = apolloClient.cache.identify({
            __typename: 'lead_agents',
            id: lead.id,
          });

          cache.updateFragment(
            {
              id: leadRef,
              fragment: LEADS_KANBAN_COLUMN_ITEM_FRAGMENT,
              fragmentName: 'LeadsKanbanColumnItem',
            },
            data => {
              if (data == null) {
                return data;
              }

              return {
                ...data,
                stage_id: newStageId,
              };
            },
          );
        });
      },
    });
  };
};
